import { CaseReducer, PayloadAction, createSlice } from '@reduxjs/toolkit';

import { get, put } from 'api/requests';
import { Toast } from '@10d/tend-ui/primitives/Toast';

import { loading } from 'store/common';

import { getPreparedFurnishingTabs } from '../helpers';
import { setFurnitureInstalledAction } from './apartmentStatusSlice';

import { IReducer, AppThunk } from 'store/types';
import {
  IFurnishingInfo,
  IOptionsMedia,
  IOptionsGroups,
  IOptionGroup,
  IFurnishingsAcceptanceMedia,
  IFurnishingsAcceptanceMediaItem,
} from 'pages/ApartmentStatus/types';

interface FurnishingState {
  loading: boolean;
  media: IOptionsMedia[];
  acceptanceMedia: IFurnishingsAcceptanceMedia[];
  info: IFurnishingInfo;
  tabs: IOptionGroup[];
}

interface FurnishingsInstallationResult {
  classifier: number;
  isFurnitureInstalled: boolean;
  isInstalled: boolean;
  media: IFurnishingsAcceptanceMediaItem[];
}

const initialState: FurnishingState = {
  loading: false,
  media: [],
  acceptanceMedia: [],
  info: {} as IFurnishingInfo,
  tabs: [],
};

const reset: IReducer<FurnishingState, FurnishingState> = () => initialState;

const setFurnishing: CaseReducer<FurnishingState, PayloadAction<IOptionsMedia[]>> = (
  state,
  action,
) => {
  state.media = action.payload;
};

const setInfo: CaseReducer<FurnishingState, PayloadAction<IFurnishingInfo>> = (state, action) => {
  state.info = action.payload;
};

const setAcceptanceMedia: CaseReducer<
  FurnishingState,
  PayloadAction<IFurnishingsAcceptanceMedia[]>
> = (state, action) => {
  state.acceptanceMedia = action.payload;
};

const setTabs: CaseReducer<FurnishingState, PayloadAction<IOptionGroup[]>> = (state, action) => {
  state.tabs = action.payload;
};

const updateInstallation: CaseReducer<
  FurnishingState,
  PayloadAction<{ classifier: number; isInstalled: boolean }>
> = (state, action) => {
  state.media = state.media.map(item =>
    item.id === action.payload.classifier
      ? { ...item, isInstalled: action.payload.isInstalled }
      : item,
  );
};

const updateAcceptanceMedia: CaseReducer<
  FurnishingState,
  PayloadAction<{ classifier: number; media: IFurnishingsAcceptanceMediaItem[] }>
> = (state, action) => {
  const group = state.acceptanceMedia.find(item => item.id === action.payload.classifier);

  if (group) {
    group.media = action.payload.media;
  } else {
    state.acceptanceMedia.push({
      id: action.payload.classifier,
      name: '',
      media: action.payload.media,
    });
  }
};

export const furnitureSlice = createSlice({
  name: 'furniture',
  initialState,
  reducers: {
    loading: loading as IReducer<FurnishingState, boolean>,
    setFurnishing,
    setAcceptanceMedia,
    setInfo,
    setTabs,
    updateInstallation,
    updateAcceptanceMedia,
    reset,
  },
});

const {
  loading: setLoading,
  setFurnishing: setFurnishingData,
  setAcceptanceMedia: setAcceptanceMediaData,
  setTabs: setTabsData,
  setInfo: setInfoData,
  updateInstallation: setInstallationData,
  updateAcceptanceMedia: updateAcceptanceMediaData,
} = furnitureSlice.actions;

export const getFurnitureMedia =
  (cardId: number): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    get<IOptionsMedia[]>(`/api/v2/standards/placements/${cardId}/options-media/`)
      .then(({ data }) => {
        dispatch(setFurnishingData(data));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const getFurnitureTabs =
  (cardId: number): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    get<IOptionsGroups>(`/api/standards/placements/${cardId}/options-groups/`)
      .then(({ data }) => {
        const preparedTabs = getPreparedFurnishingTabs(data);
        dispatch(setTabsData(preparedTabs));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const getFurnishingsAcceptanceMedia =
  (placementId: number): AppThunk =>
  dispatch => {
    get<IFurnishingsAcceptanceMedia[]>(
      `/api/standards/placements/${placementId}/furnishings-acceptance-media/`,
    ).then(({ data }) => {
      dispatch(setAcceptanceMediaData(data));
    });
  };
export const getFurnitureInfo =
  (cardId: number): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    get<IFurnishingInfo>(`/api/standards/placements/${cardId}/furnishings-info/`)
      .then(({ data }) => {
        dispatch(setInfoData(data));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const putFurnishingsInstallation =
  (
    placementId: number,
    classifier: number,
    isInstalled: boolean,
    media?: Array<Pick<IFurnishingsAcceptanceMediaItem, 'fileName' | 'link'>>,
  ): AppThunk<Promise<void>> =>
  dispatch => {
    return put<FurnishingsInstallationResult>(
      `/api/standards/placements/${placementId}/furnishings-installation/`,
      { classifier, isInstalled, ...(media !== undefined ? { media } : {}) },
    )
      .then(({ data }) => {
        dispatch(
          setInstallationData({ classifier: data.classifier, isInstalled: data.isInstalled }),
        );
        dispatch(updateAcceptanceMediaData({ classifier: data.classifier, media: data.media }));
        dispatch(
          setFurnitureInstalledAction({
            placementId,
            isFurnitureInstalled: data.isFurnitureInstalled,
          }),
        );
      })
      .catch(error => {
        Toast.error({ message: 'Не удалось подтвердить установку мебели' });
        return Promise.reject(error);
      });
  };
